import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Drawer, Skeleton, Text} from '@chakra-ui/react3'
import BadgeGroup from '../../../../components/badge-group'
import ProductDisplayPrice from '../../../../components/product-display-price'
import ProductAvailability from '../../../../leap/components/product-availability'
import {useDerivedProduct} from '../../../../hooks'
import AgeRange from '../../../../leap/components/age-range'
import {hasBVProductRating} from '../../../../components/bazaarvoice/utils'
import BVProductRating from '../../../../components/bazaarvoice/rating'
import NotifyMeBtn from '../../../../pages/product-detail/partials/xleap/ctas/notify-me'
import {useAddToCart} from '../../../../hooks/use-add-to-cart'
import useProductSpecialOffers from '../../../../hooks/use-product-special-offers'
import {FormattedMessage} from 'react-intl'
import {productUrlBuilder} from '../../../../utils/url'
import {XpdpConfiguratorSteps} from '../../utils/configurator-constants'
import useOcapiSite from '../../../../hooks/useOcapiSite'
import Slider from '../../../../leap/components/cms/slider'
import Asset from '../../../../leap/components/cms/asset'
import StackableImage from '../../../../leap/components/stackable-image'
import useXleapGalleryImages from '../../../../leap/hooks/useXleapGalleryImages'
import useStackedImages from '../../../../leap/hooks/useStackedImages'
import useMultiSite from '../../../../hooks/use-multi-site'
import useProductDetail from '../../../../hooks/useProductDetail'
import {useUnifiedProduct} from '../../../../hooks/use-unified-product'
import DisplayPrice from '../../../../leap/components/display-price'
import Loading from '../../../../leap/components/loading-container'
import {Router, useLocation} from 'react-router-dom'
import {createMemoryHistory} from 'history'
import {isProductSet} from '../../../../utils/product-utils'
import ColorSwatches from '../../../../leap/components/color-swatches'
import PromotionBanner from '../../../../leap/components/promotion-banner'
import {Chakra3MobileOnlyProvider} from '../../../../leap/theme'
import lazyLoadable from '../../../../utils/lazy-loadable'

const fallback = <Skeleton height="25vh" width="100%" />
const LazyCMSModules = lazyLoadable(
    () => import('../../../../pages/product-detail/partials/cms-modules'),
    {
        fallback,
        ssr: false
    }
)

const Quickview = ({
    product: smallProductData,
    step,
    enableCmsMidFunnel = false,
    stackableProducts,
    onNotifyMe: _onNotifyMe,
    onSubmit: _onSubmit,
    onAddToCart: _onAddToCart
}) => {
    const {
        buildUrl,
        locale: {id: locale}
    } = useMultiSite()

    const {search} = useLocation()
    const initialProductId = useMemo(
        () => new URLSearchParams(search).get('pid') || smallProductData?.id,
        [smallProductData, search]
    )

    const {
        master: masterProduct,
        variant: variantProduct,
        isVariantLoading,
        isPriceLoading
    } = useProductDetail(initialProductId)

    const unifiedProduct = useUnifiedProduct(masterProduct, variantProduct)

    const product = useMemo(
        () => ({
            ...smallProductData,
            // in the context of quickview we deal with individual products, no set products
            setProductId: null,
            ...unifiedProduct
        }),
        [smallProductData, unifiedProduct]
    )

    const isSelected = smallProductData.isSetProductSelected

    const derivedProduct = useDerivedProduct(product)
    const {bvEnabled, redirectLinkPartnerAllProducts, pdpContentEnabled} = useOcapiSite()
    const hasProductRating = useMemo(
        () => Boolean(bvEnabled) && hasBVProductRating(product),
        [bvEnabled, product]
    )
    const {promotions, loading: isPromoLoading} = useProductSpecialOffers(product, null, 'xpdp')
    const hasPromo = !isPromoLoading && !!promotions?.length

    const detailsHref = useMemo(
        () => buildUrl(productUrlBuilder(product, locale)),
        [buildUrl, product, locale]
    )

    const isStackedImagesEnabled =
        step === XpdpConfiguratorSteps.Accessories &&
        stackableProducts?.length > 0 &&
        Boolean(product?.c_imageStacked)

    // Include current quickview product last so its color wins when mapping brandmachine bases.
    const stackedImageProducts = useMemo(
        () => [...(stackableProducts ?? []), product],
        [stackableProducts, product]
    )

    const stackedImage = useStackedImages({
        imageStacked: product?.c_imageStacked,
        products: stackedImageProducts,
        enabled: isStackedImagesEnabled
    })

    const galleryItems = useXleapGalleryImages(
        product,
        derivedProduct,
        isStackedImagesEnabled ? stackedImage : undefined
    )

    const redirectToPartner = useMemo(
        () =>
            redirectLinkPartnerAllProducts &&
            JSON.parse(redirectLinkPartnerAllProducts)?.[locale.replace('-', '_')],
        [redirectLinkPartnerAllProducts, locale]
    )

    const {isOrderable, redirectLinkPartner} = derivedProduct
    const isOrderableOnOtherSites = redirectLinkPartner || redirectToPartner

    const shouldLoadCmsMidFunnel =
        enableCmsMidFunnel &&
        product?.c_pdpBottomContent &&
        pdpContentEnabled &&
        product?.c_isLeapPdpBottomContent

    const {handleAddToCart, isLoading: isAddToCartLoading} = useAddToCart(product, {
        isLoading: isPriceLoading
    })
    const onAddToCart = useCallback(
        () => handleAddToCart({onAfterAddToCart: () => _onAddToCart?.(product)}),
        [handleAddToCart, _onAddToCart, product]
    )

    const onNotifyMe = () => _onNotifyMe?.(product)
    const onSubmit = () => _onSubmit?.(product, product.id)
    const showsColorSwatches = !step || step === XpdpConfiguratorSteps.Accessories
    const titleToRatingGap = 'spacing-24'
    const sectionGap = {
        base: 'spacing-24',
        s: step === XpdpConfiguratorSteps.Accessories ? 'spacing-32' : 'spacing-24'
    }

    const headerSection = (
        <Box display="flex" flexDirection="column" gap="spacing-16">
            <Box display="flex" justifyContent="space-between">
                <AgeRange product={product} variant="configurator-quickview" />
                <BadgeGroup product={product} max={1} />
            </Box>

            <Box display="flex" flexDirection="column" gap={titleToRatingGap}>
                <Box display="flex" flexDirection="column" gap="spacing-8">
                    <Text color="text.primary" textStyle="heading.xxlarge">
                        {product?.name}
                    </Text>
                    {step === XpdpConfiguratorSteps.Configuration && (
                        <Text color="text.tertiary" textStyle="body.medium">
                            {product?.c_slogan}
                        </Text>
                    )}
                </Box>

                {hasProductRating && <BVProductRating product={product} variant="cockpit" />}
            </Box>
        </Box>
    )
    const secondarySection =
        showsColorSwatches || hasPromo ? (
            <Box display="flex" flexDirection="column" gap="spacing-24">
                {showsColorSwatches && <ColorSwatches.Root asChild product={product} />}
                {hasPromo && <PromotionBanner product={product} />}
            </Box>
        ) : null

    const detailsSection = (
        <Box
            display="flex"
            flexDirection="column"
            gap={sectionGap}
            paddingBottom={showsColorSwatches ? 'spacing-62' : sectionGap}
        >
            <Text color="text.secondary" textStyle="body.small">
                {product?.shortDescription}
            </Text>

            {showsColorSwatches && (
                <Button
                    as="a"
                    variant="secondaryGhost"
                    size="lg"
                    href={detailsHref}
                    target="_blank"
                >
                    <Text>
                        <FormattedMessage id="global.view_details" defaultMessage="View details" />
                    </Text>
                </Button>
            )}
        </Box>
    )

    return (
        <>
            <Drawer.Body>
                <Box display="flex" flexDirection="column">
                    <Loading.Root>
                        <Loading.Status>{isVariantLoading && <Loading.Spinner />}</Loading.Status>
                        <Loading.Content>
                            <Slider
                                key={product?.id || initialProductId}
                                navigationButtonSize="sm"
                                variant="configuratorQuickview"
                            >
                                {galleryItems.map((item, index) =>
                                    item?.type === 'stacked' ? (
                                        <StackableImage
                                            key={index}
                                            alt={item.alt}
                                            images={item.images}
                                            product={product}
                                        />
                                    ) : (
                                        <Asset
                                            asset={item}
                                            key={index}
                                            variant="configuratorQuickview"
                                            objectFit="cover"
                                        />
                                    )
                                )}
                            </Slider>
                        </Loading.Content>
                    </Loading.Root>

                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={sectionGap}
                        paddingInline="spacing-24"
                        pt="spacing-24"
                    >
                        {headerSection}
                        {secondarySection}

                        {detailsSection}
                    </Box>

                    {/* CMS mid-funnel  */}
                    {shouldLoadCmsMidFunnel && (
                        <Box>
                            <Chakra3MobileOnlyProvider>
                                <LazyCMSModules assetId={product.c_pdpBottomContent} />
                            </Chakra3MobileOnlyProvider>
                        </Box>
                    )}
                </Box>
            </Drawer.Body>

            <Drawer.Footer>
                <Box display="flex" justifyContent="space-between">
                    <ProductDisplayPrice
                        as={DisplayPrice}
                        product={product}
                        loading={isPriceLoading}
                        showVatLabel={true}
                    />
                    {!isProductSet(product) && (
                        <ProductAvailability
                            alignSelf="flex-end"
                            loading={isPriceLoading}
                            flexShrink="0"
                            product={product}
                        />
                    )}
                </Box>

                {isOrderable || isOrderableOnOtherSites ? (
                    <Button
                        variant="primaryFilled"
                        disabled={isVariantLoading || isAddToCartLoading}
                        loading={isVariantLoading || isAddToCartLoading}
                        width="auto"
                        size="lg"
                        onClick={step ? onSubmit : onAddToCart}
                    >
                        <Text>
                            {!step ? (
                                <FormattedMessage
                                    id="product_view.button.add_to_cart"
                                    defaultMessage="Add to Cart"
                                />
                            ) : step === XpdpConfiguratorSteps.Configuration ? (
                                <FormattedMessage id="global.select" defaultMessage="Select" />
                            ) : step === XpdpConfiguratorSteps.Accessories ? (
                                isSelected ? (
                                    <FormattedMessage
                                        id="form_action_buttons.button.save"
                                        defaultMessage="Save"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="configurator.button.add_accessory"
                                        defaultMessage="Add accessory"
                                    />
                                )
                            ) : null}
                        </Text>
                    </Button>
                ) : (
                    <NotifyMeBtn
                        variant="secondaryOutline"
                        disabled={isVariantLoading}
                        loading={isVariantLoading}
                        width="auto"
                        size="lg"
                        onClick={onNotifyMe}
                    />
                )}
            </Drawer.Footer>
        </>
    )
}

Quickview.propTypes = {
    product: PropTypes.object,
    step: PropTypes.oneOf([XpdpConfiguratorSteps.Configuration, XpdpConfiguratorSteps.Accessories]),
    stackableProducts: PropTypes.array,
    onNotifyMe: PropTypes.func,
    onSubmit: PropTypes.func,
    onAddToCart: PropTypes.func,
    enableCmsMidFunnel: PropTypes.bool
}

export const QuickviewWithRouter = (props) => {
    const memoryHistory = useMemo(
        () =>
            createMemoryHistory({
                initialEntries: [`/?pid=${props.product.id}`]
            }),
        [props.product.id]
    )

    return (
        <Router key={props.product.id} history={memoryHistory}>
            <Quickview {...props} />
        </Router>
    )
}

QuickviewWithRouter.propTypes = Quickview.propTypes

export default QuickviewWithRouter
